export const LEGACY_ALL_PROFILE_ID = '__ALL__';
export const VIRTUAL_ALL_PROFILE_ID = 'all_profiles';

export const normalizeProfileId = (profileId) => (
	profileId === LEGACY_ALL_PROFILE_ID ? VIRTUAL_ALL_PROFILE_ID : profileId
);

export const normalizeProfileIds = (profileIds = []) => (
	Array.from(new Set((profileIds ?? []).map(normalizeProfileId).filter(Boolean)))
);

export const getProfileIdsFromEntity = (entity) => {
	let rawIds = [];
	if (Array.isArray(entity?.profileIds)) {
		rawIds = entity.profileIds;
	} else if (entity?.profileId) {
		rawIds = [entity.profileId];
	}
	return normalizeProfileIds(rawIds);
};

export const getRealProfileIds = (profiles) => normalizeProfileIds(
	(profiles ?? [])
		.map((profile) => profile?.id)
		.filter((profileId) => profileId && profileId !== VIRTUAL_ALL_PROFILE_ID)
);

export const hasAllProfilesScope = (entity) => (
	getProfileIdsFromEntity(entity).includes(VIRTUAL_ALL_PROFILE_ID)
);

export const includesProfile = (entity, profileId) => {
	if (!profileId) return false;
	const normalizedProfileId = normalizeProfileId(profileId);
	const entityProfileIds = getProfileIdsFromEntity(entity);

	if (entityProfileIds.includes(VIRTUAL_ALL_PROFILE_ID)) {
		return normalizedProfileId !== VIRTUAL_ALL_PROFILE_ID;
	}

	return entityProfileIds.includes(normalizedProfileId);
};

export const includesAllProfiles = (entity, profiles) => {
	const entityProfileIds = getProfileIdsFromEntity(entity);
	if (entityProfileIds.includes(VIRTUAL_ALL_PROFILE_ID)) {
		return true;
	}

	const realProfileIds = getRealProfileIds(profiles);
	if (realProfileIds.length === 0) {
		return false;
	}

	return realProfileIds.every((profileId) => entityProfileIds.includes(profileId));
};

export const expandProfileIdsForSelection = (selectedProfileIds, profiles) => {
	const normalizedSelection = normalizeProfileIds(selectedProfileIds ?? []);
	const allRealProfileIds = getRealProfileIds(profiles);

	if (normalizedSelection.includes(VIRTUAL_ALL_PROFILE_ID)) {
		return allRealProfileIds;
	}

	return normalizedSelection.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID);
};

export const persistProfileIdsForSave = (selectedProfileIds, profiles) => {
	const normalizedSelection = normalizeProfileIds(selectedProfileIds ?? []);
	if (normalizedSelection.includes(VIRTUAL_ALL_PROFILE_ID)) {
		return [VIRTUAL_ALL_PROFILE_ID];
	}

	const explicitIds = normalizedSelection.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID);
	const allRealProfileIds = getRealProfileIds(profiles);
	if (
		allRealProfileIds.length > 0
		&& explicitIds.length === allRealProfileIds.length
		&& allRealProfileIds.every((profileId) => explicitIds.includes(profileId))
	) {
		return [VIRTUAL_ALL_PROFILE_ID];
	}

	return explicitIds;
};

export const getInitialProfileSelection = (activeProfile, profiles) => {
	const realIds = getRealProfileIds(profiles);
	if (activeProfile?.id === VIRTUAL_ALL_PROFILE_ID) {
		return [VIRTUAL_ALL_PROFILE_ID, ...realIds];
	}
	if (activeProfile?.id && activeProfile.id !== VIRTUAL_ALL_PROFILE_ID) {
		return [activeProfile.id];
	}
	return realIds.length ? [realIds[0]] : [];
};

export const initProfileSelectionFromPersisted = (persistedProfileIds, profiles) => {
	const ids = getProfileIdsFromEntity({profileIds: persistedProfileIds});
	if (ids.includes(VIRTUAL_ALL_PROFILE_ID)) {
		return [VIRTUAL_ALL_PROFILE_ID, ...getRealProfileIds(profiles)];
	}
	return ids.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID);
};

export const resolveInitialProfileSelection = (persistedProfileIds, profiles, activeProfile) => {
	const fromPersisted = initProfileSelectionFromPersisted(persistedProfileIds, profiles);
	if (fromPersisted.length > 0) {
		return fromPersisted;
	}
	return getInitialProfileSelection(activeProfile, profiles);
};

export const toggleProfileInSelection = (currentSelection, toggledProfileId, profiles) => {
	const realIds = getRealProfileIds(profiles);
	const normalizedToggledId = normalizeProfileId(toggledProfileId);

	if (normalizedToggledId === VIRTUAL_ALL_PROFILE_ID) {
		if (currentSelection.includes(VIRTUAL_ALL_PROFILE_ID)) {
			return [];
		}
		return [VIRTUAL_ALL_PROFILE_ID, ...realIds];
	}

	if (currentSelection.includes(VIRTUAL_ALL_PROFILE_ID)) {
		return realIds.filter((profileId) => profileId !== normalizedToggledId);
	}

	const isSelected = currentSelection.includes(normalizedToggledId);
	const next = isSelected
		? currentSelection.filter((profileId) => profileId !== normalizedToggledId)
		: [...currentSelection.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID), normalizedToggledId];

	if (realIds.length > 0 && realIds.every((profileId) => next.includes(profileId))) {
		return [VIRTUAL_ALL_PROFILE_ID, ...realIds];
	}

	return next;
};

export const isProfileSelectedInSelection = (selectedProfileIds, profileId, profiles) => {
	const normalizedProfileId = normalizeProfileId(profileId);
	if (normalizedProfileId === VIRTUAL_ALL_PROFILE_ID) {
		return selectedProfileIds.includes(VIRTUAL_ALL_PROFILE_ID);
	}
	if (selectedProfileIds.includes(VIRTUAL_ALL_PROFILE_ID)) {
		return true;
	}
	return selectedProfileIds.includes(normalizedProfileId);
};

export const getProfileMultiSelectLabel = (selectedProfileIds, profiles) => {
	if (selectedProfileIds.includes(VIRTUAL_ALL_PROFILE_ID)) {
		return 'ALL profiles';
	}

	const selectedRealIds = selectedProfileIds.filter((profileId) => profileId !== VIRTUAL_ALL_PROFILE_ID);
	if (selectedRealIds.length === 0) {
		return 'Select profiles';
	}
	if (selectedRealIds.length === 1) {
		const profile = (profiles ?? []).find((item) => item.id === selectedRealIds[0]);
		return profile?.profileName ?? '1 profile';
	}
	return `${selectedRealIds.length} profiles`;
};

export const removeProfileFromScope = (profileIds, profileIdToRemove, profiles) => {
	const ids = getProfileIdsFromEntity({profileIds});
	if (ids.includes(VIRTUAL_ALL_PROFILE_ID)) {
		return getRealProfileIds(profiles).filter((profileId) => profileId !== profileIdToRemove);
	}
	return ids.filter((profileId) => profileId !== profileIdToRemove);
};
